import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  FileText, 
  Download, 
  UploadCloud, 
  Filter, 
  Plus, 
  X, 
  CheckCircle2, 
  AlertCircle,
  FolderOpen
} from 'lucide-react';
import { DOCUMENT_CATEGORIES, DocumentCategory } from '@ftt/shared';

export const ClientDocumentsPage: React.FC = () => {
  const [selectedCat, setSelectedCat] = useState<string>('ALL');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadCategory, setUploadCategory] = useState<DocumentCategory>('MANUSCRIPT');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data: documentsRes, isLoading } = useQuery({
    queryKey: ['client-documents', selectedCat],
    queryFn: () => {
      let url = '/client/documents';
      if (selectedCat !== 'ALL') url += `?category=${selectedCat}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!uploadFile) throw new Error('Please select a file to upload.');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle || uploadFile.name);
      formData.append('category', uploadCategory);
      return api.post('/client/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-documents'] });
      queryClient.invalidateQueries({ queryKey: ['client-dashboard'] });
      setIsUploadModalOpen(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadError(null);
    },
    onError: (err: any) => {
      setUploadError(err.response?.data?.error?.message || err.message || 'Upload failed.');
    },
  });

  const documents = documentsRes || [];

  return (
    <div className="space-y-8">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900">
            Document Centre
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Secure author repository for contracts, editorial proofs, cover art, and production files.
          </p>
        </div>

        <button
          onClick={() => setIsUploadModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs shadow-sm transition-colors self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Upload File / Revision</span>
        </button>
      </div>

      {/* Category Filter Bar */}
      <div className="flex flex-wrap gap-2 p-3 bg-white rounded-xl border border-editorial-border shadow-xs text-xs font-medium">
        <button
          onClick={() => setSelectedCat('ALL')}
          className={`px-3 py-1.5 rounded-md font-semibold transition-colors ${
            selectedCat === 'ALL' ? 'bg-editorial-dark text-white' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          All Files ({documents.length})
        </button>
        {DOCUMENT_CATEGORIES.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setSelectedCat(cat.value)}
            className={`px-3 py-1.5 rounded-md font-semibold transition-colors ${
              selectedCat === cat.value ? 'bg-editorial-dark text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Documents Table / Grid */}
      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-500">Loading document repository...</div>
      ) : documents.length === 0 ? (
        <div className="p-12 rounded-2xl bg-white border border-editorial-border text-center space-y-3">
          <FolderOpen className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-slate-800">No Documents Found</h3>
          <p className="text-xs text-slate-500">
            No files currently found in this category.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-editorial-border shadow-xs overflow-hidden">
          <div className="divide-y divide-slate-100">
            {documents.map((doc: any) => (
              <div
                key={doc.id}
                className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/70 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-50 border border-brand-200 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-brand-700" />
                  </div>
                  <div className="space-y-1 overflow-hidden">
                    <h4 className="font-serif font-bold text-sm text-slate-900 truncate">{doc.title}</h4>
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500 font-mono">
                      <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                        {doc.category}
                      </span>
                      <span>&bull;</span>
                      <span>{Math.round(doc.fileSize / 1024)} KB</span>
                      <span>&bull;</span>
                      <span>Uploaded {new Date(doc.createdAt).toLocaleDateString()}</span>
                      {doc.project && (
                        <>
                          <span>&bull;</span>
                          <span className="text-brand-700 font-semibold">{doc.project.projectCode}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 self-end sm:self-center">
                  <a
                    href={`/api/client/documents/${doc.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-editorial-dark hover:bg-slate-800 text-white font-semibold text-xs transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-slate-200 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-serif text-xl font-bold text-slate-900">Upload Author Document</h3>
              <button
                onClick={() => setIsUploadModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {uploadError && (
              <div className="p-3.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{uploadError}</span>
              </div>
            )}

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Document Title (Optional)
                </label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="e.g. Chapter 4 Revised Manuscript"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Document Category
                </label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value as DocumentCategory)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-hidden bg-white"
                >
                  {DOCUMENT_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Select File (PDF, DOCX, EPUB, RTF, ZIP) *
                </label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="w-full text-xs text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-800 hover:file:bg-brand-100"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setIsUploadModalOpen(false)}
                className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => uploadMutation.mutate()}
                disabled={!uploadFile || uploadMutation.isPending}
                className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs disabled:opacity-50 flex items-center gap-1.5"
              >
                {uploadMutation.isPending ? 'Uploading...' : 'Save to Document Centre'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
